import { useEffect, useState } from "react";
import {
  Bell,
  ChartSpline,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  HelpCircle,
  LayoutDashboard,
  MapPinned,
  MessageSquareText,
  Moon,
  Settings,
  Sun,
} from "lucide-react";
import { useLocation } from "react-router-dom";

import { Avatar } from "@/components/catalyst/avatar";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import {
  Sidebar,
  SidebarBody,
  SidebarDivider,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
} from "@/components/catalyst/sidebar";
import { useAuthStore } from "@/store/auth";
import { useThemeStore } from "@/store/theme";

import iconBlack from "@/assets/images/xaptronics-icon-black-crisp.png";
import iconWhite from "@/assets/images/xaptronics-icon-white-crisp.png";
import logoBlack from "@/assets/images/xaptronics-logo-black-crisp.png";
import logoWhite from "@/assets/images/xaptronics-logo-white-crisp.png";

const analyticsChildren = [
  { label: "Fleet Overview", href: "/analytics/fleet" },
  { label: "Battery", href: "/analytics/battery" },
  { label: "Alarms", href: "/analytics/alarms" },
  { label: "Thermal & Cells", href: "/analytics/thermal" },
  { label: "Movement", href: "/analytics/movement" },
];

const reportChildren = [
  { label: "Device Inventory", href: "/reports/device-inventory" },
  { label: "Battery Performance", href: "/reports/battery-performance" },
  { label: "Alarm Compliance", href: "/reports/alarm-compliance" },
  { label: "Thermal & Cell", href: "/reports/thermal-cell" },
  {
    label: "Movement & Connectivity",
    href: "/reports/movement-connectivity",
  },
];

const primaryNavItems = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
];

const secondaryNavItems = [
  { label: "Alerts", href: "/alerts", icon: Bell },
  { label: "Track Your Device", href: "/tracking", icon: MapPinned },
  { label: "Settings", href: "/admin", icon: Settings },
];

interface AppSidebarProps {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

function ResponsiveLabel({
  children,
  collapsed,
}: {
  children: React.ReactNode;
  collapsed: boolean;
}) {
  return (
    <SidebarLabel className={collapsed ? "lg:hidden" : undefined}>
      {children}
    </SidebarLabel>
  );
}

function NavGroup({
  label,
  icon: Icon,
  basePath,
  children,
  collapsed,
  pathname,
  open,
  onToggle,
}: {
  label: string;
  icon: typeof ChartSpline;
  basePath: string;
  children: { label: string; href: string }[];
  collapsed: boolean;
  pathname: string;
  open: boolean;
  onToggle: () => void;
}) {
  const isActiveGroup = pathname.startsWith(basePath);

  if (collapsed) {
    return (
      <SidebarItem
        href={children[0]?.href ?? basePath}
        current={isActiveGroup}
        title={label}
        className="lg:justify-center"
      >
        <Icon data-slot="icon" className="size-5" />
        <ResponsiveLabel collapsed={collapsed}>{label}</ResponsiveLabel>
      </SidebarItem>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`flex w-full cursor-pointer items-center gap-3 rounded-lg
          px-2 py-2.5 text-left text-base/6 font-medium transition
          sm:py-2 sm:text-sm/5 ${
            isActiveGroup
              ? "text-blue-600 dark:text-blue-300"
              : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5"
          }`}
      >
        <Icon className="size-5 shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        <ChevronDown
          className={`size-4 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="mt-1 space-y-0.5 border-l border-slate-200 pl-3
          dark:border-white/10"
        >
          {children.map((child) => (
            <SidebarItem
              key={child.href}
              href={child.href}
              current={pathname === child.href}
              className="text-sm"
            >
              <SidebarLabel className="truncate">
                {child.label}
              </SidebarLabel>
            </SidebarItem>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppSidebar({
  collapsed = false,
  onToggleCollapsed,
}: AppSidebarProps) {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const theme = useThemeStore((state) => state.theme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);
  const isMobileSidebar = !onToggleCollapsed;
  const routeGroup = location.pathname.startsWith("/analytics")
    ? "analytics"
    : location.pathname.startsWith("/reports")
      ? "reports"
      : null;
  const [openGroup, setOpenGroup] = useState<
    "analytics" | "reports" | null
  >(routeGroup);
  const isRoot = user?.role === "supersuperadmin";
  const canViewAnalytics = Boolean(
    isRoot || user?.permissions?.includes("view_analytics"),
  );
  const canViewReports = Boolean(
    isRoot || user?.permissions?.includes("view_reports"),
  );

  useEffect(() => {
    setOpenGroup(routeGroup);
  }, [routeGroup]);

  const toggleGroup = (group: "analytics" | "reports") => {
    setOpenGroup((current) => current === group ? null : group);
  };

  const initials = user
    ? `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() ||
      user.email[0]?.toUpperCase()
    : "?";

  return (
    <Sidebar className="border-r border-slate-200 bg-gradient-to-b from-white via-slate-50 to-blue-50/60 text-slate-700 dark:border-slate-800 dark:bg-gradient-to-b dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/30 dark:text-slate-200">
      <SidebarHeader className="border-slate-200 dark:border-white/10">
        <div className="flex min-h-12 items-center justify-center px-1 py-1">
          {collapsed ? (
            <>
              <img
                src={iconBlack}
                alt="Xaptronics"
                className="hidden h-10 w-auto object-contain lg:block dark:lg:hidden"
              />
              <img
                src={iconWhite}
                alt="Xaptronics"
                className="hidden h-10 w-auto object-contain dark:lg:block"
              />
            </>
          ) : (
            <>
              <img
                src={logoBlack}
                alt="Xaptronics"
                className="h-10 w-auto max-w-full object-contain dark:hidden"
              />
              <img
                src={logoWhite}
                alt="Xaptronics"
                className="hidden h-10 w-auto max-w-full object-contain dark:block"
              />
            </>
          )}
        </div>
      </SidebarHeader>

      <SidebarBody>
        <SidebarSection>
          {primaryNavItems.map((item) => (
            <SidebarItem
              key={item.href}
              href={item.href}
              current={location.pathname.startsWith(item.href)}
              title={collapsed ? item.label : undefined}
              className={collapsed ? "lg:justify-center" : undefined}
            >
              <item.icon data-slot="icon" className="size-5" />
              <ResponsiveLabel collapsed={collapsed}>
                {item.label}
              </ResponsiveLabel>
            </SidebarItem>
          ))}

          {canViewAnalytics && (
            <NavGroup
              label="Analytics"
              icon={ChartSpline}
              basePath="/analytics"
              children={analyticsChildren}
              collapsed={collapsed}
              pathname={location.pathname}
              open={openGroup === "analytics"}
              onToggle={() => toggleGroup("analytics")}
            />
          )}

          {canViewReports && (
            <NavGroup
              label="Reports"
              icon={FileSpreadsheet}
              basePath="/reports"
              children={reportChildren}
              collapsed={collapsed}
              pathname={location.pathname}
              open={openGroup === "reports"}
              onToggle={() => toggleGroup("reports")}
            />
          )}

          {secondaryNavItems.map((item) => (
            <SidebarItem
              key={item.href}
              href={item.href}
              current={location.pathname.startsWith(item.href)}
              title={collapsed ? item.label : undefined}
              className={collapsed ? "lg:justify-center" : undefined}
            >
              <item.icon data-slot="icon" className="size-5" />
              <ResponsiveLabel collapsed={collapsed}>
                {item.label}
              </ResponsiveLabel>
            </SidebarItem>
          ))}
        </SidebarSection>
      </SidebarBody>

      <SidebarFooter className="border-slate-200 dark:border-white/10">
        <SidebarSection>
          <SidebarItem
            href="/help"
            title={collapsed ? "Help" : undefined}
            className={collapsed ? "lg:justify-center" : undefined}
          >
            <HelpCircle data-slot="icon" className="size-5" />
            <ResponsiveLabel collapsed={collapsed}>Help</ResponsiveLabel>
          </SidebarItem>
          <SidebarItem
            href="/contact"
            title={collapsed ? "Contact Us" : undefined}
            className={collapsed ? "lg:justify-center" : undefined}
          >
            <MessageSquareText data-slot="icon" className="size-5" />
            <ResponsiveLabel collapsed={collapsed}>Contact Us</ResponsiveLabel>
          </SidebarItem>
          {isMobileSidebar && (
            <SidebarItem
              onClick={toggleTheme}
              title={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
              className="cursor-pointer"
            >
              {theme === "dark" ? (
                <Sun data-slot="icon" className="size-5" />
              ) : (
                <Moon data-slot="icon" className="size-5" />
              )}
              <SidebarLabel>
                {theme === "dark" ? "Light mode" : "Dark mode"}
              </SidebarLabel>
            </SidebarItem>
          )}
        </SidebarSection>

        <SidebarDivider />

        <div
          className={`flex items-center gap-1 ${
            collapsed ? "lg:flex-col" : ""
          }`}
        >
          <Dropdown>
            <DropdownButton
              as={SidebarItem}
              title={collapsed ? (user?.email ?? "Account") : undefined}
              className={`min-w-0 flex-1 ${
                collapsed ? "lg:justify-center" : ""
              }`}
            >
              <Avatar
                initials={initials}
                className="bg-blue-100 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-400/15 dark:text-blue-200 dark:ring-blue-400/20"
                square
              />
              <ResponsiveLabel collapsed={collapsed}>
                {user?.email ?? "Account"}
              </ResponsiveLabel>
            </DropdownButton>
            <DropdownMenu anchor="top start">
              <DropdownItem href="/profile">
                <DropdownLabel>Profile</DropdownLabel>
              </DropdownItem>
              <DropdownDivider />
              <DropdownItem onClick={logout}>
                <DropdownLabel>Sign out</DropdownLabel>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>

          {onToggleCollapsed && (
            <button
              type="button"
              className="hidden size-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-blue-100 hover:text-blue-700 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white lg:flex"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
              title={collapsed ? "Expand navigation" : "Collapse navigation"}
            >
              {collapsed ? (
                <ChevronRight className="size-4" />
              ) : (
                <ChevronLeft className="size-4" />
              )}
            </button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
